import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, TextT, VideoCamera, CurrencyCircleDollar, ArrowSquareOut, DeviceMobile, LockSimple,
  QrCode, CreditCard, Power, FloppyDisk, Spinner, Eye,
} from "@phosphor-icons/react";
import { api, brl, errMsg } from "@/lib/api";
import { PUBLIC_BASE_URL } from "@/lib/publicUrl";
import { useFeeRates } from "@/hooks/useFeeRates";
import { Slider } from "@/components/ui/slider";
import { VideoBuyStage } from "@/components/videobuy/VideoBuyStage";
import { EditorSection, Field, ToggleRow, PhonePreview, inputCls, isUrl } from "@/components/videobuy/EditorBits";

const EMPTY = {
  title: "", headline: "", slug: "", video_url: "", video_vertical: false, price: 47,
  button_text: "Comprar agora", redirect_url: "", require_watch: false, require_watch_percent: 50,
  accept_pix: true, accept_card: true, active: true,
};

const validate = (f) => {
  const e = {};
  if (f.title.trim().length < 2) e.title = "Informe um título";
  if (!isUrl(f.video_url)) e.video_url = "Cole uma URL válida (https://...)";
  if (!(Number(f.price) >= 1)) e.price = "Preço mínimo R$ 1,00";
  if (!isUrl(f.redirect_url)) e.redirect_url = "Informe o link de destino (https://...)";
  if (!f.accept_pix && !f.accept_card) e.methods = "Ative ao menos Pix ou Cartão";
  return e;
};

export default function VideoPageEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const editing = !!id;
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const { rates } = useFeeRates();
  const set = (patch) => setF((s) => ({ ...s, ...patch }));
  const errors = useMemo(() => validate(f), [f]);
  const err = (k) => (touched ? errors[k] : undefined);

  useEffect(() => {
    if (!editing) return;
    api.get(`/video-pages/${id}`)
      .then(({ data }) => setF({ ...EMPTY, ...data }))
      .catch((e) => { toast.error(errMsg(e, "Página não encontrada")); nav("/app/funnels"); })
      .finally(() => setLoading(false));
  }, [id, editing, nav]);

  useEffect(() => {
    const t = setTimeout(() => setPreviewUrl(isUrl(f.video_url) ? f.video_url.trim() : ""), 700);
    return () => clearTimeout(t);
  }, [f.video_url]);

  const save = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (Object.keys(errors).length) return toast.error(Object.values(errors)[0]);
    setSaving(true);
    const payload = { ...f, price: Number(f.price), require_watch_percent: Number(f.require_watch_percent), slug: f.slug || null };
    try {
      if (editing) await api.put(`/video-pages/${id}`, payload);
      else await api.post("/video-pages", payload);
      toast.success(editing ? "Página atualizada!" : "Página publicada! Copie o link e comece a vender.");
      nav("/app/funnels");
    } catch (er) { toast.error(errMsg(er, "Erro ao salvar")); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div className="py-24 flex justify-center" data-testid="video-editor-loading"><Spinner size={32} className="animate-spin text-[var(--accent)]" /></div>;
  }

  const net = Number(f.price || 0) * (1 - (rates.sales_commission_rate || 0));
  const preview = (
    <PhonePreview>
      <VideoBuyStage key={`${previewUrl}-${f.video_vertical}`} compact data={{ ...f, video_url: previewUrl }} onBuy={() => toast.info("Prévia: no link público abre o checkout")} />
    </PhonePreview>
  );

  return (
    <div className="fadeUp">
      <Link to="/app/funnels" className="btn-ghost text-xs inline-flex items-center gap-1 mb-4" data-testid="video-editor-back"><ArrowLeft size={14} /> Voltar</Link>
      <div className="label-caps mb-2">Venda direta</div>
      <h1 className="font-display font-black text-3xl sm:text-4xl md:text-5xl tracking-tighter">{editing ? "Editar página de vídeo" : "Nova página de vídeo + compra"}</h1>
      <p className="text-white/60 mt-2 mb-8 text-sm sm:text-base">Vídeo em cima, botão de compra embaixo. Pagou → o cliente vai direto para o seu link.</p>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_380px] gap-8 items-start">
        <form onSubmit={save} className="space-y-5 min-w-0" data-testid="video-page-form">
          <EditorSection icon={TextT} title="Conteúdo da página" desc="O que o cliente lê antes do vídeo.">
            <Field label="Título da oferta *" error={err("title")}>
              <input className={inputCls} value={f.title} onChange={(e) => set({ title: e.target.value })} placeholder="Ex: Método Vendas no Automático" maxLength={140} data-testid="editor-input-title" />
            </Field>
            <Field label="Chamada (opcional)" hint="Uma frase curta que reforça o benefício.">
              <textarea className={`${inputCls} min-h-[84px] resize-y`} value={f.headline} onChange={(e) => set({ headline: e.target.value })} placeholder="Ex: Assista ao vídeo e descubra como faturar todos os dias" maxLength={400} data-testid="editor-input-headline" />
            </Field>
            <Field label="Endereço do link (opcional)" hint="Deixe em branco para gerarmos automaticamente.">
              <div className="flex items-stretch rounded-xl border border-white/10 bg-black/30 focus-within:border-[#FFB30F]/60 overflow-hidden">
                <span className="hidden sm:flex items-center px-3 text-xs text-slate-500 border-r border-white/10 shrink-0 font-mono">{PUBLIC_BASE_URL.replace("https://", "")}/v/</span>
                <input className="flex-1 min-w-0 bg-transparent px-3 py-3 text-[15px] outline-none font-mono" value={f.slug || ""} onChange={(e) => set({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} placeholder="minha-oferta" maxLength={60} data-testid="editor-input-slug" />
              </div>
            </Field>
          </EditorSection>

          <EditorSection icon={VideoCamera} title="Vídeo" desc="YouTube, Vimeo ou MP4 direto — com proteção anti-download.">
            <Field label="URL do vídeo *" error={err("video_url")}>
              <input className={inputCls} value={f.video_url} onChange={(e) => set({ video_url: e.target.value })} placeholder="https://youtube.com/watch?v=..." data-testid="editor-input-video-url" />
            </Field>
            <ToggleRow icon={DeviceMobile} title="Vídeo vertical (9:16)" desc="Ideal para Reels, Shorts e TikTok." checked={f.video_vertical} onChange={(v) => set({ video_vertical: v })} testid="editor-toggle-vertical" />
            <ToggleRow icon={LockSimple} title="Travar botão até assistir" desc="O botão de compra só libera após o cliente assistir parte do vídeo." checked={f.require_watch} onChange={(v) => set({ require_watch: v })} testid="editor-toggle-watch-gate" />
            {f.require_watch && (
              <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] px-4 py-4">
                <div className="flex justify-between text-sm mb-3"><span className="text-slate-300">Liberar após assistir</span><b className="text-[#FFB30F] tabular-nums" data-testid="editor-watch-percent-value">{f.require_watch_percent}%</b></div>
                <Slider min={5} max={100} step={5} value={[Number(f.require_watch_percent)]} onValueChange={([v]) => set({ require_watch_percent: v })} data-testid="editor-slider-watch-percent" />
              </div>
            )}
          </EditorSection>

          <EditorSection icon={CurrencyCircleDollar} title="Oferta e pagamento" desc="Preço e formas de pagamento aceitas.">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Preço (R$) *" error={err("price")} hint={`Você recebe ${brl(net)} por venda`}>
                <input type="number" min="1" step="0.01" inputMode="decimal" className={inputCls} value={f.price} onChange={(e) => set({ price: e.target.value })} data-testid="editor-input-price" />
              </Field>
              <Field label="Texto do botão">
                <input className={inputCls} value={f.button_text} onChange={(e) => set({ button_text: e.target.value })} maxLength={60} data-testid="editor-input-button-text" />
              </Field>
            </div>
            <ToggleRow icon={QrCode} title="Aceitar Pix" desc="Mercado Pago · aprovação imediata." checked={f.accept_pix} onChange={(v) => set({ accept_pix: v })} testid="editor-toggle-pix" />
            <ToggleRow icon={CreditCard} title="Aceitar Cartão de crédito" desc="Checkout seguro Stripe." checked={f.accept_card} onChange={(v) => set({ accept_card: v })} testid="editor-toggle-card" />
            {err("methods") && <div className="text-xs text-red-400" data-testid="editor-methods-error">{err("methods")}</div>}
          </EditorSection>

          <EditorSection icon={ArrowSquareOut} title="Depois do pagamento" desc="Para onde o cliente vai assim que o pagamento for aprovado.">
            <Field label="Link de redirecionamento *" error={err("redirect_url")} hint="Ex: área de membros, grupo VIP, Google Drive, página de obrigado.">
              <input className={inputCls} value={f.redirect_url} onChange={(e) => set({ redirect_url: e.target.value })} placeholder="https://seusite.com/obrigado" data-testid="editor-input-redirect-url" />
            </Field>
            {isUrl(f.redirect_url) && (
              <a href={f.redirect_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[#FFB30F] hover:underline" data-testid="editor-test-redirect-link"><ArrowSquareOut size={13} /> Testar link</a>
            )}
          </EditorSection>

          <ToggleRow icon={Power} title="Página ativa" desc="Desative para pausar as vendas sem apagar a página." checked={f.active} onChange={(v) => set({ active: v })} testid="editor-toggle-active" />

          <button type="button" onClick={() => setShowPreview((s) => !s)} className="lg:hidden btn-secondary w-full justify-center" data-testid="editor-toggle-preview-btn">
            <Eye size={16} /> {showPreview ? "Ocultar prévia" : "Ver prévia no celular"}
          </button>
          {showPreview && <div className="lg:hidden">{preview}</div>}

          <button disabled={saving} className="btn-primary w-full justify-center !py-4 text-base" data-testid="editor-save-page-btn">
            {saving ? <Spinner size={18} className="animate-spin" /> : <FloppyDisk size={18} weight="fill" />}
            {editing ? "Salvar alterações" : "Publicar página"}
          </button>
        </form>

        <aside className="hidden lg:block sticky top-6">
          <div className="label-caps mb-3 text-center">Prévia ao vivo</div>
          {preview}
        </aside>
      </div>
    </div>
  );
}
